import React, { useEffect, useMemo, useState } from "react";
import BusinessIcon from "@mui/icons-material/Business";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import PersonIcon from "@mui/icons-material/Person";
import { useLocation, useParams } from "react-router-dom";

import { api, ApiError } from "../../api/apiClient";
import { StaticData, useStaticData } from "../../context/StaticDataContext";
import PartyFamily, {
  FamilyContext,
} from "../features/manage-party/PartyFamily";
import PartyOrganisation from "../features/manage-party/PartyOrganisation";
import PartyPerson from "../features/manage-party/PartyPerson";
import {
  PartyFormMode,
  PartyFormProvider,
  PartyFormContextValue,
  PartyType,
} from "../features/manage-party/PartyFormContext";
import {
  OrganisationDetails,
  PersonDetails,
  RegistrationMemberType,
  ServiceRegistrationRequirementDto,
} from "../types/types";
import AgreementCard, {
  Agreement,
} from "../features/manage-party/components/AgreementCard";
import ConsentCard, {
  Consent,
} from "../features/manage-party/components/ConsentCard";
import "../../css/forms.css";
import "../features/manage-party/PartyManagement.css";

interface PublicConfig {
  serviceId?: number | null;
  serviceName?: string | null;
  partyType?: PartyType | null;
  staticData: StaticData;
  agreements: PublicServiceAgreement[];
  registrationRequirements: ServiceRegistrationRequirementDto[];
}

interface PublicServiceAgreement {
  serviceAgreementTemplateId: number;
  name: string;
  text: string;
  helpText?: string | null;
  agreementType?: string | null;
  requiresAcceptance: boolean;
}

interface PublicProfile {
  partyType: PartyType;
  serviceId?: number | null;
  serviceName?: string | null;
  person?: PersonDetails;
  organisation?: OrganisationDetails;
  family?: FamilyContext;
  agreementResponses?: Array<{
    serviceAgreementTemplateId: number;
    accepted: boolean | null;
  }>;
}

interface RouteParams {
  token?: string;
}

const parsePartyType = (value: string | null): PartyType | null => {
  const normalized = value?.toLowerCase();
  if (normalized === "person" || normalized === "individual") return "Person";
  if (normalized === "family") return "Family";
  if (normalized === "organisation" || normalized === "organization") {
    return "Organisation";
  }
  return null;
};

export default function PublicPartyPage() {
  const location = useLocation();
  const { token } = useParams<RouteParams>();
  const { setPublicData } = useStaticData();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedPartyType = parsePartyType(query.get("partyType"));
  const requestedServiceIdValue = Number(query.get("serviceId"));
  const requestedServiceId =
    Number.isInteger(requestedServiceIdValue) && requestedServiceIdValue > 0
      ? requestedServiceIdValue
      : undefined;
  const requestedMemberType =
    (query.get("memberType") as RegistrationMemberType | null) ?? "ServiceUser";
  const mode = token
    ? PartyFormMode.ProfileCompletion
    : PartyFormMode.PublicRegistration;

  const [partyType, setPartyType] = useState<PartyType | null>(requestedPartyType);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [completed, setCompleted] = useState(false);
  const [exitOutcome, setExitOutcome] = useState<"saved" | "unchanged" | null>(null);
  const [agreementValues, setAgreementValues] = useState<Agreement[]>([]);
  const [consentValues, setConsentValues] = useState<Consent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setError(null);
        if (!token && !requestedServiceId) {
          setError("This registration link is missing a valid service ID.");
          return;
        }

        const loadedProfile = token
          ? await api<PublicProfile>(
              `/api/public/profile/${encodeURIComponent(token)}`,
              { auth: "none" },
            )
          : null;
        const serviceId = loadedProfile?.serviceId ?? requestedServiceId;
        const loadedConfig = await api<PublicConfig>(
          `/api/public/registration/config?memberType=${encodeURIComponent(requestedMemberType)}${
            serviceId ? `&serviceId=${serviceId}` : ""
          }`,
          { auth: "none" },
        );

        if (cancelled) return;
        setConfig(loadedConfig);
        const consentTemplates = loadedConfig.agreements.filter((item) =>
          item.agreementType?.toLowerCase().includes("consent") ||
          item.name.toLowerCase().includes("consent") ||
          item.name.toLowerCase().includes("sharing of data"),
        );
        setAgreementValues(
          loadedConfig.agreements
            .filter((item) => !consentTemplates.includes(item))
            .map((item) => ({
              agreementCode: String(item.serviceAgreementTemplateId),
              title: item.name,
              text: item.text,
              accepted:
                loadedProfile?.agreementResponses?.find(
                  (response) =>
                    response.serviceAgreementTemplateId ===
                    item.serviceAgreementTemplateId,
                )?.accepted === true,
            })),
        );
        setConsentValues(
          consentTemplates.map((item) => ({
            consentTypeCode: String(item.serviceAgreementTemplateId),
            consentTypeName: item.name,
            question: item.helpText ?? item.text,
            granted:
              loadedProfile?.agreementResponses?.find(
                (response) =>
                  response.serviceAgreementTemplateId ===
                  item.serviceAgreementTemplateId,
              )?.accepted ?? null,
          })),
        );
        setPublicData(loadedConfig.staticData);
        if (loadedProfile) {
          setProfile(loadedProfile);
          setPartyType(loadedProfile.partyType);
        } else if (loadedConfig.partyType) {
          setPartyType(loadedConfig.partyType);
        }
      } catch (loadError) {
        if (cancelled) return;
        setError(
          loadError instanceof ApiError && loadError.status === 404
            ? "This invitation is invalid or has expired."
            : "Registration could not be loaded. Please try again later.",
        );
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [requestedMemberType, requestedServiceId, setPublicData, token]);

  const context: PartyFormContextValue = {
    mode,
    partyType: partyType ?? undefined,
    serviceId: config?.serviceId ?? profile?.serviceId ?? undefined,
    serviceName: config?.serviceName ?? profile?.serviceName ?? undefined,
    invitationToken: token,
    allowPartySearch: false,
    allowMerge: false,
    allowDelete: false,
    allowPartyTypeChange:
      mode === PartyFormMode.PublicRegistration &&
      !requestedPartyType &&
      !config?.partyType,
    allowServiceSelection: false,
    allowViewInternalIds: false,
    allowNavigation: false,
    agreementResponses: [
      ...agreementValues.map((item) => ({
        serviceAgreementTemplateId: Number(item.agreementCode),
        accepted: item.accepted,
      })),
      ...consentValues.map((item) => ({
        serviceAgreementTemplateId: Number(item.consentTypeCode),
        accepted: item.granted,
      })),
    ],
    registrationRequirements: config?.registrationRequirements ?? [],
  };

  const closeProfile = (outcome: "saved" | "unchanged") => {
    window.close();
    setExitOutcome(outcome);
  };

  const handleCompleted = () => {
    if (mode === PartyFormMode.ProfileCompletion) {
      closeProfile("saved");
    } else {
      setCompleted(true);
    }
  };

  if (error) return <div className="page-container"><div className="notification notification--error">{error}</div></div>;
  if (!config) return <div className="page-container"><div className="card">Loading registration...</div></div>;
  if (exitOutcome) return <div className="page-container"><div className="card"><h1>{exitOutcome === "saved" ? "Profile updated" : "No changes"}</h1><p>{exitOutcome === "saved" ? "Your changes were saved successfully. You can now close this page." : "No changes were saved. You can now close this page."}</p></div></div>;
  if (completed) return <div className="page-container"><div className="card"><h1>Thank you</h1><p>Your registration has been submitted for review. Your details will only be added after an administrator approves it.</p></div></div>;

  const agreementsContent = agreementValues.length > 0 ? (
    <AgreementCard
      agreements={agreementValues}
      onChange={setAgreementValues}
    />
  ) : null;
  const consentsContent = consentValues.length > 0 ? (
    <ConsentCard
      child={{
        id: "registration",
        firstName: "",
        lastName: "",
        dateOfBirth: "",
      }}
      consents={consentValues}
      onChange={setConsentValues}
    />
  ) : null;

  return (
    <PartyFormProvider value={context}>
      <div className="page-container">
        <div className="card">
          <div className="card-header-row">
            <div>
              <h1 className="page-header-title">
                {config.serviceName ?? "FrontLine Registration"}
              </h1>
              <p className="page-header-subtitle">
                {mode === PartyFormMode.ProfileCompletion
                  ? "Complete your profile"
                  : "Register your details"}
              </p>
            </div>
          </div>
        </div>

        {context.allowPartyTypeChange && (
          <div className="tab-button-row party-management-tabs">
            <button type="button" className={`party-management-tab ${partyType === "Person" ? "active" : ""}`} onClick={() => setPartyType("Person")}><PersonIcon /><span>Individual</span></button>
            <button type="button" className={`party-management-tab ${partyType === "Family" ? "active" : ""}`} onClick={() => setPartyType("Family")}><FamilyRestroomIcon /><span>Family</span></button>
            <button type="button" className={`party-management-tab ${partyType === "Organisation" ? "active" : ""}`} onClick={() => setPartyType("Organisation")}><BusinessIcon /><span>Organisation</span></button>
          </div>
        )}

        {!partyType && <div className="card"><div className="field-hint">Choose who you are registering.</div></div>}
        {partyType === "Organisation" && agreementValues.length > 0 && (
          <AgreementCard agreements={agreementValues} onChange={setAgreementValues} />
        )}
        {partyType === "Organisation" && consentValues.length > 0 && (
          <ConsentCard
            child={{ id: "registration", firstName: "", lastName: "", dateOfBirth: "" }}
            consents={consentValues}
            onChange={setConsentValues}
          />
        )}
        {partyType === "Person" && <PartyPerson partyId={null} resetSignal={0} initialDetails={profile?.person} onCompleted={handleCompleted} agreementsContent={agreementsContent} consentsContent={consentsContent} showCancel={mode === PartyFormMode.ProfileCompletion} onCancel={() => closeProfile("unchanged")} saveLabel={mode === PartyFormMode.ProfileCompletion ? "Save and close" : "Save"} cancelLabel="No changes" />}
        {partyType === "Organisation" && <PartyOrganisation partyId={null} resetSignal={0} initialDetails={profile?.organisation} onCompleted={handleCompleted} showCancel={mode === PartyFormMode.ProfileCompletion} onCancel={() => closeProfile("unchanged")} saveLabel={mode === PartyFormMode.ProfileCompletion ? "Save and close" : "Save"} cancelLabel="No changes" />}
        {partyType === "Family" && (
          <PartyFamily
            partyId={null}
            initialContext={profile?.family}
            agreementTemplates={agreementValues}
            consentTemplates={consentValues}
            onCompleted={handleCompleted}
            onCancel={mode === PartyFormMode.ProfileCompletion ? () => closeProfile("unchanged") : undefined}
            saveLabel={mode === PartyFormMode.ProfileCompletion ? "Save and close" : "Save"}
            cancelLabel="No changes"
          />
        )}
      </div>
    </PartyFormProvider>
  );
}
